import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getHouderDetail, getPutten, getGebieden, maakInrichtingHandmatig } from '../api/api';
import { fmtId } from '../util/format';
import DoelenKiezer from '../components/DoelenKiezer';
import PuttenKaart from '../components/PuttenKaart';

/* Vergunning aanmaken.

   Dit was de vergunningtak van een formulier dat ook meldingen deed. Die twee
   stonden in één scherm met een radioknop erboven die het halve formulier
   omzette, en met een eigen puttenlijst zonder kaart — geen gebiedscheck, geen
   perceel, geen kandidaten. Nu is het één ding dat één ding doet, met hetzelfde
   puttenpaneel als de meldingpagina.

   ── Waarom een vergunning wél overal mag ──────────────────────────────────

   Bij een melding blokkeren kwetsbare gebieden: daar ontstaat geen recht van
   rechtswege, dus daar valt niets te registreren. Een vergunning is precies het
   omgekeerde — het bevoegd gezag heeft de afweging gemaakt en die kan ook voor
   een kwetsbaar gebied positief uitvallen. Vandaar `kwetsbaarBlokkeert={false}`:
   je ziet nog steeds als label waar de put ligt, maar er wordt niets geweigerd.

   Ligt een put in een waterstaatswerk, dan vraagt het paneel om het
   besluitnummer van de omgevingsvergunning. Dat veld zat er al voor meldingen
   en doet hier hetzelfde.

   ── En waarom de diepte hier niet uit het doel volgt ──────────────────────

   Bij een melding bepaalt het doel welke keurkaartlaag geldt, en daarmee de
   maximale boordiepte; daar staat het puttenpaneel dicht tot er een doel is.
   Hier staat de diepte in de beschikking. De twee lagen zijn naslag — ze staan
   bij de put én bij een aangeklikte lege plek — en de behandelaar vult in wat
   vergund is. Vandaar `dieptesUitDoel={false}`: de kaart is meteen open en het
   doel is weer optioneel. */

const LEEG = {
  besluitnummer: '',
  ingangsdatum: '',
  doelen: [],
  categorieen: [],
  vergund_debiet_uur: '',
  vergund_debiet_maand: '',
  vergund_debiet_kwartaal: '',
  vergund_debiet_jaar: '',
  bedrijfsomvang_ha: '',
  opmerkingen: '',
};

const getal = (v) => (v === '' || v == null ? null : Number(v));

export default function VergunningNieuw() {
  const { houderId } = useParams();
  const navigate = useNavigate();

  const [houder, setHouder] = useState(null);
  const [form, setForm] = useState(LEEG);
  const [putten, setPutten] = useState([]);
  const [registerPutten, setRegisterPutten] = useState([]);
  const [gebieden, setGebieden] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const [laadFout, setLaadFout] = useState(null);

  useEffect(() => {
    getHouderDetail(houderId)
      .then((r) => setHouder(r.data.houder))
      .catch((e) => setLaadFout(e.response?.data?.detail || e.message));
    getPutten()
      .then((r) => setRegisterPutten((r.data || []).filter((p) => !p.buiten_gebruik_datum)))
      .catch(() => {});
    getGebieden(undefined, true).then((r) => setGebieden(r.data || [])).catch(() => {});
  }, [houderId]);

  const zet = (veld, waarde) => setForm((f) => ({ ...f, [veld]: waarde }));

  const opslaan = async () => {
    setFout(null);
    if (!form.besluitnummer.trim()) return setFout('Besluitnummer is verplicht.');
    if (!form.ingangsdatum) return setFout('Ingangsdatum is verplicht.');
    if (putten.length === 0) return setFout('Een vergunning zonder putten kan niet.');

    setBezig(true);
    try {
      const body = {
        houder_id: houderId,
        verzoeknummer: form.besluitnummer.trim(),
        rechtstype: 'vergunning',
        kenmerk_type: 'besluitnummer',
        meldingsdatum: null,
        ingangsdatum: form.ingangsdatum,
        doelen: form.doelen || [],
        doel: (form.doelen || [])[0] || null,
        vergund_debiet_uur: getal(form.vergund_debiet_uur),
        vergund_debiet_maand: getal(form.vergund_debiet_maand),
        vergund_debiet_kwartaal: getal(form.vergund_debiet_kwartaal),
        vergund_debiet_jaar: getal(form.vergund_debiet_jaar),
        bedrijfsomvang_ha: getal(form.bedrijfsomvang_ha),
        opmerkingen: form.opmerkingen.trim() || null,
        /* Het puttenpaneel kent drie soorten. 'gekoppeld' is een put die al in
           het register staat; die blijft zoals hij is en wordt alleen
           gekoppeld. De andere twee worden nieuwe registerputten — het verschil
           tussen 'nog te boren' en 'bestaat al maar stond er niet in' zit in of
           er een werkelijke diepte bekend is, niet in wat het register ervan
           maakt. */
        putten: putten.map((p) => (p.soort === 'gekoppeld'
          ? {
            type: 'bestaand',
            put_id: p.put_id,
            /* Alleen als het register de werkelijke diepte nog niet kende en
               de behandelaar hem hier heeft ingevuld. De backend vult dan aan;
               een bestaande waarde overschrijven doet hij niet. */
            ...(p.opgegevenDiepteLeeg && String(p.opgegeven_diepte_m || '').trim()
              ? { opgegeven_diepte_m: getal(p.opgegeven_diepte_m) }
              : {}),
          }
          : {
            type: 'nieuw',
            x_rd: Number(p.x_rd),
            y_rd: Number(p.y_rd),
            perceel: (p.perceel || '').trim() || null,
            max_diepte_mv: getal(p.max_diepte_mv),
            opgegeven_diepte_m: getal(p.opgegeven_diepte_m),
            diepte_bv_filter_m: getal(p.diepte_bv_filter_m),
            besluitnummer_wsw: (p.besluitnummer || '').trim() || null,
          })),
      };
      const res = await maakInrichtingHandmatig(body);
      navigate(`/beregening/inrichtingen/${res.data.inrichting_id}`);
    } catch (e) {
      setFout(e.response?.data?.detail || e.message);
    } finally {
      setBezig(false);
    }
  };

  if (laadFout) {
    return (
      <div className="main-inner">
        <div className="alert alert-error" style={{ margin: '0 32px' }}>{laadFout}</div>
      </div>
    );
  }
  if (!houder) {
    return <div className="empty-state"><span className="spinner" /> Laden…</div>;
  }

  const houderNaam = houder.naam
    || [houder.voornaam, houder.tussenvoegsel, houder.achternaam].filter(Boolean).join(' ')
    || fmtId(houderId);

  return (
    <div className="main-inner main-inner-wide">
      <div className="page-header">
        <div>
          <p className="page-subtitle" style={{ marginBottom: 4 }}>
            <Link to={`/beregening/houders/${houderId}`}>← {houderNaam}</Link>
          </p>
          <h1 className="page-title">Vergunning aanmaken</h1>
          <p className="page-subtitle">
            {houderNaam} <span className="mono">{fmtId(houderId)}</span>
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Besluit</h3></div>
        <div className="field-grid">
          <div className="field">
            <label className="field-label">Besluitnummer</label>
            <input
              value={form.besluitnummer}
              onChange={(e) => zet('besluitnummer', e.target.value)}
              placeholder="bv. WB-V-2025-0042"
            />
          </div>
          <div className="field">
            <label className="field-label">Ingangsdatum</label>
            <input
              type="date"
              value={form.ingangsdatum}
              onChange={(e) => zet('ingangsdatum', e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label">Bedrijfsomvang (ha)</label>
            <input
              type="number" step="0.01" value={form.bedrijfsomvang_ha}
              onChange={(e) => zet('bedrijfsomvang_ha', e.target.value)}
            />
          </div>
        </div>
        <div className="field" style={{ marginTop: 20 }}>
          <label className="field-label">Opmerkingen</label>
          <textarea rows={3} value={form.opmerkingen}
            onChange={(e) => zet('opmerkingen', e.target.value)} />
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Doelen</h3></div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Optioneel. Selecteer eerst de hoofdcategorieën, daarna de bijbehorende
          sub-doelen.
        </div>
        <DoelenKiezer
          categorieen={form.categorieen}
          doelen={form.doelen}
          onChange={({ categorieen, doelen }) => setForm((f) => ({ ...f, categorieen, doelen }))}
        />
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Vergund maximum debiet</h3></div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Alle vier optioneel. Vul in wat in de beschikking staat.
        </div>
        <div className="field-grid">
          <div className="field">
            <label className="field-label">Vergund uurdebiet (m³)</label>
            <input type="number" step="0.01" value={form.vergund_debiet_uur}
              onChange={(e) => zet('vergund_debiet_uur', e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Vergund maanddebiet (m³)</label>
            <input type="number" step="0.01" value={form.vergund_debiet_maand}
              onChange={(e) => zet('vergund_debiet_maand', e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Vergund kwartaaldebiet (m³)</label>
            <input type="number" step="0.01" value={form.vergund_debiet_kwartaal}
              onChange={(e) => zet('vergund_debiet_kwartaal', e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Vergund jaardebiet (m³)</label>
            <input type="number" step="0.01" value={form.vergund_debiet_jaar}
              onChange={(e) => zet('vergund_debiet_jaar', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card mf-kaart">
        <PuttenKaart
          putten={putten}
          onChange={setPutten}
          registerPutten={registerPutten}
          gebieden={gebieden}
          hoogte={520}
          kwetsbaarBlokkeert={false}
          dieptesUitDoel={false}
        />
      </div>

      {fout && <div className="alert alert-error" style={{ marginTop: 4 }}>{fout}</div>}

      <footer className="mf-voet">
        <button className="btn btn-ghost" onClick={() => navigate(`/beregening/houders/${houderId}`)}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
          {bezig ? <><span className="spinner" /> Opslaan…</> : 'Vergunning aanmaken'}
        </button>
      </footer>
    </div>
  );
}
